/** Format seconds-per-km as m:ss (never m:60 — rolls 60s into next minute). */
export function formatPaceSecPerKm(paceSeconds: number): string {
  if (!paceSeconds || !Number.isFinite(paceSeconds) || paceSeconds <= 0) return '--:--';
  const totalSec = Math.max(0, Math.round(paceSeconds));
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/** Format elapsed seconds as H:MM:SS or M:SS (never trailing :60). */
export function formatElapsedTime(totalSeconds: number): string {
  const totalSec = Math.max(0, Math.round(totalSeconds));
  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/** Speech-friendly elapsed time — always includes seconds when under one hour. */
export function formatElapsedSpeech(totalSeconds: number): string {
  const totalSec = Math.max(0, Math.round(totalSeconds));
  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  if (hours > 0) {
    const secPart =
      seconds > 0 ? ` and ${seconds} second${seconds !== 1 ? 's' : ''}` : '';
    return `${hours} hour${hours !== 1 ? 's' : ''}, ${minutes} minute${minutes !== 1 ? 's' : ''}${secPart}`;
  }
  if (minutes > 0 && seconds > 0) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''} and ${seconds} second${seconds !== 1 ? 's' : ''}`;
  }
  if (minutes > 0) return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  return `${seconds} second${seconds !== 1 ? 's' : ''}`;
}

/** Speech-friendly pace — never emits invalid values like "4:60". */
export function formatPaceSpeech(
  secPerKm: number,
  units: "metric" | "imperial" = "metric",
): string {
  if (!secPerKm || !Number.isFinite(secPerKm)) return '';
  const KM_PER_MILE = 1.609344;
  const totalSec = Math.max(
    0,
    Math.round(units === "imperial" ? secPerKm * KM_PER_MILE : secPerKm),
  );
  const unitWord = units === "imperial" ? "mile" : "kilometer";
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  if (m <= 0) return `${s} seconds per ${unitWord}`;
  if (s === 0) return `${m} minute${m !== 1 ? 's' : ''} per ${unitWord}`;
  return `${m} minute${m !== 1 ? 's' : ''} and ${s} seconds per ${unitWord}`;
}
